import { create } from "zustand";
import { getAPIEndpoint } from "./API";
import { isProd } from "./ContentAPI";
import { postJsonWithRetry } from "./util/NetRetry";
const MAX_RECORDS = 50;
const IS_PROD = (() => {
    try {
        return isProd();
    }
    catch (_a) {
        // Fallback for test environment where window is not available
        return false;
    }
})();
export const useLogger = create()((set, get) => ({
    records: [],
    pushRecord: (message) => {
        const state = get();
        state.records.push(message);
        if (state.records.length > MAX_RECORDS) {
            state.records.shift();
        }
        set(state);
    },
    flush: () => {
        const state = get();
        const transportRecord = {
            url: window.location.href,
            timestamp: new Date().getTime(),
            records: state.records,
        };
        state.records = [];
        set(state);
        sendLogsRequest(transportRecord);
    },
    log: (message) => {
        if (!IS_PROD) {
            console.log(message);
        }
        get().pushRecord(message);
    },
    error: (message) => {
        console.error(message);
        const state = get();
        state.pushRecord(message);
        if (!IS_PROD) {
            // Debug aid only. In prod this line re-emitted the entire 50-record
            // INFO buffer prefixed with "ERROR:", which the daily lexical failure
            // scan then counted wholesale as failures.
            console.log("ERROR: " + JSON.stringify(state.records));
        }
        state.flush();
    },
    warn: (message) => {
        console.warn(message);
        get().pushRecord(message);
    },
    recordPostView: (channelOwnerId, channelId, postId) => {
        const sessionKey = `postView_${channelOwnerId}_${channelId}_${postId}`;
        // Check if we've already recorded this view in this session
        if (sessionStorage.getItem(sessionKey)) {
            console.log("Post view already recorded in this session");
            return;
        }
        console.log(`Recording post view: channelOwnerId=${channelOwnerId}, channelId=${channelId}, postId=${postId}`);
        // Mark as recorded in this session
        sessionStorage.setItem(sessionKey, "1");
        const payload = {
            operation: "recordPostView",
            channelOwnerId: channelOwnerId,
            channelId: channelId,
            postId: postId,
            timestamp: new Date().getTime(),
            records: [], // Empty records array for consistency with interface
        };
        sendPostViewRequest(payload);
    },
    recordPostClick: (channelOwnerId, channelId, postId) => {
        const sessionKey = `postClick_${channelOwnerId}_${channelId}_${postId}`;
        // Check if we've already recorded this click in this session
        if (sessionStorage.getItem(sessionKey)) {
            console.log("Post click already recorded in this session");
            return;
        }
        console.log(`Recording post click: channelOwnerId=${channelOwnerId}, channelId=${channelId}, postId=${postId}`);
        // Mark as recorded in this session
        sessionStorage.setItem(sessionKey, "1");
        const payload = {
            operation: "recordPostClick",
            channelOwnerId: channelOwnerId,
            channelId: channelId,
            postId: postId,
            timestamp: new Date().getTime(),
            records: [], // Empty records array for consistency with interface
        };
        sendPostClickRequest(payload);
    },
}));
// Shared transport for the three notifyLogs senders below. Network-level
// failures (XHR status 0 — connection never established) are retried with
// backoff by postJsonWithRetry; HTTP errors are not, since they reached the
// server. Failures are reported via console only — routing them through
// logger.error would flush() and ship another notifyLogs request, an
// amplification loop during exactly the outages that cause status 0.
const sendNotifyLogs = (label, payload) => {
    console.log(label + ": payload = " + JSON.stringify(payload));
    try {
        postJsonWithRetry({
            url: getAPIEndpoint() + "notifyLogs",
            body: JSON.stringify(payload),
            headers: { "Content-type": "application/json" },
            onRetryScheduled: (attemptNumber, delayMs) => {
                console.warn(label +
                    ": network failure (status 0) on attempt " +
                    attemptNumber +
                    ", retrying in " +
                    delayMs +
                    "ms");
            },
            onDone: (status, responseText) => {
                console.log(label + ": HTTP status: " + status);
                console.log(label + ": status = " + status);
                if (status >= 200 && status < 300) {
                    console.log(label + ": responseText: " + responseText);
                }
                else {
                    console.error(label + ": responseText: " + responseText);
                    // Check if this is a sequence number error - if so, ignore it to prevent thrashing
                    if (responseText && responseText.includes("sequence number")) {
                        console.log(label + ": Ignoring sequence number error to prevent thrashing");
                    }
                }
            },
        });
    }
    catch (err) {
        console.error(label + ": " + err);
    }
};
export const sendLogsRequest = (payload) => {
    payload["operation"] = "reportError";
    sendNotifyLogs("sendLogsRequest", payload);
};
export const sendPostViewRequest = (payload) => {
    sendNotifyLogs("sendPostViewRequest", payload);
};
export const sendPostClickRequest = (payload) => {
    sendNotifyLogs("sendPostClickRequest", payload);
};
